import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiResponse, User } from "../../types/apiTypes";
import { setData, clearState } from "../reducers/userSlice";

import { Dispatch, Action } from "@reduxjs/toolkit";
import { normalBaseQuery } from "../BaseQuery";

// Generic function that handles mutation responses for side effects.
export async function handleMutationResult<T, R>(
  dispatch: Dispatch<Action>,
  queryFulfilled: Promise<{ data: T }>,
  actionCreator: (payload: R) => Action,
  responseMapper: (data: T) => R
): Promise<void> {
  try {
    const { data } = await queryFulfilled;
    const payload = responseMapper(data);
    dispatch(actionCreator(payload));
  } catch (error) {
    console.error("Mutation error:", error);
  }
}

export const loginApi = createApi({
  reducerPath: "loginApi",
  baseQuery: normalBaseQuery,

  endpoints: (builder) => ({
    loginGoogle: builder.query<{ url: string }, void>({
      query: () => ({
        url: "login/google",
        method: "POST",
      }),
    }),
    loginEmailPassword: builder.mutation<
      ApiResponse<{ accessToken: string; user: User }>,
      { email: string; password: string }
    >({
      query: ({ email, password }) => ({
        url: "login/email",
        method: "POST",
        body: { email, password },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await handleMutationResult(dispatch, queryFulfilled, setData, ({ data }) => ({
          user: data.user,
          accessToken: data.accessToken,
        }));
      },
    }),
    getUserAndRefreshToken: builder.query<ApiResponse<{ user: User }>, { accessToken: string }>({
      query: ({ accessToken }) => ({
        url: "login/get_user",
        method: "GET",
        headers: {
          Authorization: `JWT ${accessToken}`,
        },
        credentials: "same-origin",
      }),
    }),
    isAuthenticated: builder.query<ApiResponse<{ isLogin: boolean }>, void>({
      query: () => ({
        url: "login/is_login",
        method: "GET",
        credentials: "same-origin",
      }),
    }),
    logout: builder.mutation<ApiResponse<undefined>, void>({
      query: () => ({
        url: "login/logout",
        method: "POST",
        credentials: "same-origin",
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await handleMutationResult(dispatch, queryFulfilled, clearState, () => ({}));
      },
    }),
    signUp: builder.mutation<
      ApiResponse<{ accessToken: string; user: User }>,
      { email: string; password: string; givenName: string; surname: string; phone_number: string }
    >({
      query: ({ email, password, givenName, surname, phone_number }) => ({
        url: "login/register",
        method: "POST",
        body: { email, password, given_name: givenName, surname, phone_number },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await handleMutationResult(dispatch, queryFulfilled, setData, ({ data }) => ({
          user: data.user,
          accessToken: data.accessToken,
        }));
      },
    }),
  }),
});

export const {
  useLoginGoogleQuery,
  useLoginEmailPasswordMutation,
  useLazyGetUserAndRefreshTokenQuery,
  useLogoutMutation,
  useSignUpMutation,
  useIsAuthenticatedQuery
} = loginApi;
